/* globals Reflect */

import React from 'react';
import {PropTypes} from 'prop-types';
import {dedent} from './helpers.jsx';
import sanitizeHTML from 'sanitize-html';

const mapMediaSizeToBootstrapGrid = (sizeMedia) => {
    const sizes = {
        "0": "0",
        "0.1": "2",
        "0.2": "3",
        "0.3": "4",
        "0.4": "5",
        "0.5": "6",
        "0.6": "7",
        "0.7": "9",
        "0.8": "10",
        "0.9": "11",
        "1": "12"
    };
    return sizes[sizeMedia];
};

const getComponentDisplayName = (Component) => (
    Component.displayName || Component.name || 'Component'
);

const handleLinkKeyDownWithFunction = (handlerFunction) => (event) => {
    if (event.key === 'Enter') {
        event.preventDefault();
        return handlerFunction(event);
    }
};

const getQueryStringParameterByName = (
    parameterName
) => {
    const safeParameterName = parameterName.replace(/[\[\]]/g, '\\$&');
    const queryStringRegex = new RegExp(`[?&]${safeParameterName}(=([^&#]*)|&|#|$)`);
    const queryStringRegexResults = queryStringRegex.exec(window.location.href);
    if (!queryStringRegexResults) {
        return;
    }
    const desiredRawQueryStringParam = queryStringRegexResults[2];
    if (!desiredRawQueryStringParam) {
        return '';
    }
    return decodeURIComponent(desiredRawQueryStringParam.replace(/\+/g, ' '));
};

// A vastly simplified version of ReactRedux's `connect` function. Assumes that
// your state is either passed in via our custom `Provider` component, or (if
// the component is not a nested within a Provider) is avaliable as
// `window.JSContext`. It also assumes that you are rendering a static page
// (your stateis not going to change)
//
// `mapJSContextToProps` is expected to be a function that takes JSContext as
// its argument, and returns the props to pass to the component.
//
// Returns a function which, when passed a component, will bind the mapped
// props into that component by wrapping it in another component and passing
// along the JSContext merged with the given props
//
// If overrideJSContext is passed to the component as part of props, the mapping
// function will be passed that as the state instead of the global JSContext
// (props.overrideJSContext should be used as a testing tool.)
const connectToJSContext = (mapJSContextToProps) => {
    return (Component) => {
        const displayName = getComponentDisplayName(Component);

        const WrappedComponent = class extends React.Component {
            render() {
                const {context, props} = this;

                const mappedProps = mapJSContextToProps(
                    context.overrideJSContext ||
                    props.overrideJSContext ||
                    // This should eventually be deprecated in favor of Provider.
                    window.JSContext,
                    props
                );
                Object.assign(mappedProps, props, { overrideJSContext: undefined });

                // See if this component has been replaced by CustomTheme
                const Replacement = (
                    context.componentOverrides &&
                    context.componentOverrides[displayName]
                ) || null;

                return Replacement || <Component {...mappedProps} />;
            }
        };

        WrappedComponent.displayName = `ConnectToJSContext(${displayName})`;

        WrappedComponent.contextTypes = {
            componentOverrides: PropTypes.object,
            overrideJSContext: PropTypes.object
        };

        // eslint-disable-next-line no-undef
        if(process.env.NODE_ENV === "test") {
            // In test mode, clean up snapshot by doing a shallow render of nested WrappedComponents
            WrappedComponent.contextTypes.usePlaceholder = PropTypes.bool;
            WrappedComponent.childContextTypes = {
                usePlaceholder: PropTypes.bool
            };
            WrappedComponent.prototype.getChildContext = function() {
                return { usePlaceholder: !!this.props.overrideJSContext };
            };
            const defaultRender = WrappedComponent.prototype.render;
            WrappedComponent.prototype.render = function() {
                if(this.context.usePlaceholder) {
                    return (
                        <div {...this.props}>
                            {dedent`${WrappedComponent.displayName} goes here!
                                This is an automatic placeholder generated in test mode.`}
                        </div>
                    );
                }
                return Reflect.apply(defaultRender, this, []);
            };
        }

        return WrappedComponent;
    };
};

// Analogues to ReactRedux.Provider. Sets the global JSContext which will be made
// avaliabe to all Hearsay Components.
class Provider extends React.Component {
    getChildContext() {
        return {overrideJSContext: this.props.JSContext};
    }

    render() {
        return React.Children.only(this.props.children);
    }
}

Provider.propTypes = {
    JSContext: PropTypes.object.isRequired,
    children: PropTypes.element.isRequired
};

Provider.childContextTypes = {
    overrideJSContext: PropTypes.object
};

// Sets the custom sections into the context and then render the original
// theme.  Connected components should respect the context and render the
// custom sections where requested.
class CustomTheme extends React.Component {

    getChildContext() {
        const componentOverrides = {};
        React.Children.forEach(this.props.children, (child) => {
            // TODO: We could validate that this is the only override for this
            // component, and that this child is actually a ComponentOverride.
            componentOverrides[child.props.replaces] = child.props.children;
        });
        return {componentOverrides};
    }

    render() {
        // Convert to variable name that starts with a capital letter to
        // appease JSX
        const Theme = this.props.extends;
        return <Theme />;
    }
}

CustomTheme.propTypes = {
    // React.createClass, functional components, & classes are all functions
    extends: PropTypes.func.isRequired
};

CustomTheme.childContextTypes = {
    componentOverrides: PropTypes.object
};

const ComponentOverride = () => {};

ComponentOverride.propTypes = {
    replaces: PropTypes.string.isRequired,
    // Only allow one child element
    children: PropTypes.element
};

class ExtensionPoint extends React.Component {
    constructor(props) {
        super(props);
        const namedComponent = () => (null);
        namedComponent.displayName = props.name;
        this.WrappedComponent = connectToJSContext(() => ({}))(namedComponent);
    }

    render() {
        const {WrappedComponent} = this;
        return <WrappedComponent />;
    }
}

ExtensionPoint.propTypes = {
    // TODO: Enforce that names start with a capital letter
    name: PropTypes.string.isRequired
};

/**
 * Get location object
 * @param {string} location
 * @returns {Object}
 */
const getLocationObject = (locationAddress) => {
    if (typeof locationAddress !== 'string') {
        return null;
    }

    const virtualLocationRegex = new RegExp('^\\s*(@)?\\s*(.*)$', 'ig');
    const match = virtualLocationRegex.exec(locationAddress);
    const [ , prefix, address ] = Array.from(match);
    const isVirtual = !!prefix;
    let gMapsUrl = null;

    if (!isVirtual) {
        gMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURI(address)}`;
    }

    return {
        isVirtual,
        address,
        gMapsUrl
    };
};


/**
 * @param {string} orgLink
 * @param {string} slug
 * @returns {string}
 */
const getSlugUrl = (orgLink, slug) => {
    if (slug && /^https?:\/\/.+/i.test(slug)) {
        return slug;
    }
    return orgLink + slug;
};

/**
 * https://hearsaysocial.atlassian.net/browse/RA-73
 * A work around for malicious code being rendered by innerHTML.  The sanitizeHTML function finds any
 * offending attributes and replaces them with empty strings.  Once all uses of `dangerouslySetInnerHTML`
 * are removed, this function should be deleted.
 * @param {string} htmlString
 * @returns {string}
 */
export const RA_73_STERILIZE_HTML_STRING = htmlString => sanitizeHTML(htmlString, {
    allowedAttributes: {
        a: [ 'href', 'name', 'target', 'aria-label', 'rel', 'role', 'title', 'hreflang' ],
        img: [ 'src', 'alt', 'title' ]
    }
});

export const parseJsonSafe = (jsonString) => {
    let jsObject = null;
    try {
        jsObject = JSON.parse(jsonString);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Not JSON: ${jsonString}`);
    }
    return jsObject;
};

export {
    mapMediaSizeToBootstrapGrid,
    getComponentDisplayName,
    getQueryStringParameterByName,
    handleLinkKeyDownWithFunction,
    connectToJSContext,
    Provider,
    CustomTheme,
    ComponentOverride,
    ExtensionPoint,
    getLocationObject,
    getSlugUrl
};



// WEBPACK FOOTER //
// ./components/utils.jsx